import { useForm } from '@inertiajs/react';
import { PublicLayout } from '@/components/PublicLayout';
import { Link } from '@inertiajs/react';

interface Subscriber {
  email: string;
  name: string | null;
  token: string;
}

interface UnsubscribeProps {
  success?: boolean;
  message?: string;
  error?: string;
  subscriber?: Subscriber;
  auth?: {
    user?: {
      id: number;
      email: string;
      name?: string;
      username?: string;
      role?: 'user' | 'admin' | 'manager';
    };
  };
}

export function Unsubscribe({ success = false, message = '', error = '', subscriber, auth }: UnsubscribeProps) {
  const { post, processing } = useForm({
    token: subscriber?.token || ''
  });

  const handleUnsubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    post('/blog/unsubscribe');
  };

  const content = (
    <>
      {success && message && (
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white rounded-lg shadow-sm border p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium mb-2">
                {message}
              </h3>
              <p className="text-sm text-muted-foreground">
                We're sorry to see you go. You can always subscribe again anytime.
              </p>
              <div className="mt-6">
                <Link
                  href="/blog"
                  className="text-primary hover:text-primary/80 font-medium"
                >
                  Back to blog
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white rounded-lg shadow-sm border p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-lg font-medium mb-2">
                {error}
              </h3>
              <p className="text-sm text-muted-foreground">
                This unsubscribe link may have expired or is invalid.
              </p>
              <div className="mt-6">
                <Link
                  href="/blog"
                  className="text-primary hover:text-primary/80 font-medium"
                >
                  Back to blog
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {subscriber && (
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white rounded-lg shadow-sm border p-8">
            <div className="text-center">
              <h2 className="text-xl font-semibold mb-2">
                Unsubscribe
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                Are you sure you want to unsubscribe from our newsletter?
              </p>
              
              <div className="mb-6 p-4 bg-muted rounded-md">
                <p className="text-sm">
                  <span className="font-medium">Email:</span> {subscriber.email}
                </p>
                {subscriber.name && (
                  <p className="text-sm">
                    <span className="font-medium">Name:</span> {subscriber.name}
                  </p>
                )}
              </div>

              <form onSubmit={handleUnsubscribe} className="space-y-4">
                <input type="hidden" name="token" value={subscriber.token} />
                
                <button
                  type="submit"
                  disabled={processing}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing ? 'Unsubscribing...' : 'Yes, unsubscribe me'}
                </button>
                
                <Link
                  href="/blog"
                  className="w-full flex justify-center py-2 px-4 border border-input rounded-md shadow-sm text-sm font-medium bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  No, keep me subscribed
                </Link>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Default fallback */}
      {!success && !error && !subscriber && (
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <Link
              href="/blog"
              className="text-primary hover:text-primary/80 font-medium"
            >
              Back to blog
            </Link>
          </div>
        </div>
      )}
    </>
  );

  return <PublicLayout user={auth?.user}>{content}</PublicLayout>;
}

export default Unsubscribe;
